import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Backdrop,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";

import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiBox from "components/SuiBox";

const ModalAcreedor = ({ open, onClose, acreedor, onSave }) => {
  const [formData, setFormData] = useState({
    id: "",
    tipo: "",
    tipo_identificacion: "",
    identificacion: "",
    nombre_completo: "",
    email: "",
    ciudad: "",
    direccion: "",
    numero_contacto: "",
    asistio: false,
    correos: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (acreedor) {
      setFormData({
        id: acreedor.id,
        tipo: acreedor.tipo || "",
        tipo_identificacion: acreedor.tipo_identificacion || "",
        identificacion: acreedor.identificacion || "",
        nombre_completo: acreedor.nombre_completo || acreedor.nombre || "",
        email: acreedor.email || "",
        ciudad: acreedor.ciudad || "",
        direccion: acreedor.direccion || "",
        numero_contacto: acreedor.numero_contacto || "",
        asistio: acreedor.asistio || false,
        correos: acreedor.correos ? [...acreedor.correos] : [],
      });
    }
  }, [acreedor]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCorreoChange = (index, value) => {
    setFormData((prev) => {
      const nuevosCorreos = [...prev.correos];
      nuevosCorreos[index].email = value;
      return { ...prev, correos: nuevosCorreos };
    });
  };

  const handleAgregarCorreo = () => {
    setFormData((prev) => ({
      ...prev,
      correos: [...prev.correos, { id: null, email: "", orden: prev.correos.length }],
    }));
  };

  const handleEliminarCorreo = (index) => {
    setFormData((prev) => ({
      ...prev,
      correos: prev.correos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nombre_completo) {
      alert("El nombre del acreedor es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {saving && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <SuiTypography variant="h5" fontWeight="bold">
            Editar Información del Acreedor
          </SuiTypography>
        </DialogTitle>

        <DialogContent>
          <SuiBox component="form" autoComplete="off" p={1}>
            {/* Campos generales */}
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Tipo de Identificación
                </SuiTypography>
                <Select
                  name="tipo_identificacion"
                  value={formData.tipo_identificacion}
                  onChange={handleChange}
                >
                  <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                  <MenuItem value="NIT">Número de Identificación Tributaria</MenuItem>
                  <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                  <MenuItem value="PAS">Pasaporte</MenuItem>
                </Select>
              </Grid>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Identificación
                </SuiTypography>
                <SuiInput
                  name="identificacion"
                  value={formData.identificacion}
                  onChange={handleChange}
                  placeholder="Número de identificación"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Nombre Completo
                </SuiTypography>
                <SuiInput
                  name="nombre_completo"
                  value={formData.nombre_completo}
                  onChange={handleChange}
                  placeholder="Nombre completo del acreedor"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Tipo
                </SuiTypography>
                <SuiInput
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  placeholder="BANCO / PERSONA / EMPRESA"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Correo Principal
                </SuiTypography>
                <SuiInput
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Correo principal"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Dirección
                </SuiTypography>
                <SuiInput
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  placeholder="Dirección completa"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Ciudad
                </SuiTypography>
                <SuiInput
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleChange}
                  placeholder="Ciudad de residencia"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Teléfono / Contacto
                </SuiTypography>
                <SuiInput
                  name="numero_contacto"
                  value={formData.numero_contacto}
                  onChange={handleChange}
                  placeholder="Número de contacto"
                  fullWidth
                />
              </Grid>

              {/* Correos adicionales */}
              <Grid size={6} sx={{ mt: 1 }}>
                <SuiTypography variant="h6" fontWeight="dark">
                  Correos Adicionales
                </SuiTypography>
              </Grid>
              <Grid size={6}>
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  size="small"
                  onClick={handleAgregarCorreo}
                >
                  <AddIcon /> Agregar Correo
                </SuiButton>
              </Grid>
              <Grid size={12}>
                {formData.correos.map((correo, index) => (
                  <Grid
                    container
                    key={index}
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1, mt: 0.5 }}
                  >
                    <Grid size={11}>
                      <SuiInput
                        value={correo.email}
                        onChange={(e) => handleCorreoChange(index, e.target.value)}
                        placeholder={`Correo adicional #${index + 1}`}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={1}>
                      <IconButton
                        color="error"
                        onClick={() => handleEliminarCorreo(index)}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </SuiBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SuiButton onClick={onClose} color="secondary">
            Cancelar
          </SuiButton>
          <SuiButton variant="gradient" buttonColor="dark" onClick={handleSubmit} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </SuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

ModalAcreedor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  acreedor: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};

export default ModalAcreedor;
